import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Image from '$partials/media/image';
import { AnimatedStatementText } from '$partials/ui/index.js';

gsap.registerPlugin(ScrollTrigger);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleAnimatedStatementLarge = ({
    alignment = 'left',
    mainText = '',
    subText = '',
    theme = '',
    backgroundImage,
    backgroundImageMobile
}) => {
    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();

    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create reveal hide of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 0.1,
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animated-statement__lines'),
                {
                    aria: 'none',
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );
        }
    };

    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    // render
    return (
        <section
            className={`animated-statement relative py-100 ${theme ? ' animated-statement--' + theme : ''}`}
            ref={containerRef}>

            {/* image */}
            {backgroundImage && (
                <div
                    aria-hidden='true'
                    className={`absolute inset-0 h-full w-full overflow-hidden ${backgroundImageMobile ? 'hidden sm:block' : ''}`}>

                    <figure className='absolute inset-0 h-full w-full object-center object-cover select-none pointer-events-none'>
                        <Image
                            {...backgroundImage}
                            className='h-full object-cover'
                            containerClassName='h-full object-cover'
                            height={'100%'}
                            width={'100%'}/>
                    </figure>
                    <div className='absolute inset-0 media-top-bottom-gradient' />
                </div>
            )}
            {backgroundImageMobile && (
                <div
                    aria-hidden='true'
                    className='absolute inset-0 h-full w-full overflow-hidden sm:hidden'>

                    <figure className='absolute inset-0 h-full w-full object-center object-cover select-none pointer-events-none'>
                        <Image
                            {...backgroundImageMobile}
                            className='h-full object-cover'
                            containerClassName='h-full object-cover'
                            height={'100%'}
                            width={'100%'}/>
                    </figure>
                    <div className='absolute inset-0 media-top-bottom-gradient' />
                </div>
            )}

            <div className={`${theme ? '' : 'animated-statement animated-statement--white'} layout-container w-full pb-20 overflow-hidden`}>
                <div className={`flex flex-wrap${alignment === 'center' ? ' text-center' : ''}`}>
                    <div className={`w-full sm:w-10/12 px-10 sm:px-5 md:px-10${alignment === 'center' ? ' m-auto' : ''}`}>
                        <div className='type-style-display break-words animated-statement__text'>
                            <AnimatedStatementText>
                                {mainText}
                            </AnimatedStatementText>
                        </div>
                    </div>

                    {subText && (
                        <div className={`w-full px-10 sm:px-5 md:px-10${alignment === 'center' ? ' m-auto sm:w-10/12 md:w-6/12' : ' sm:w-10/12 md:w-8/12'}`}>
                            <div className='type-style-5 mt-24 md:mt-43 break-words animated-statement__text'>
                                <AnimatedStatementText>
                                    {subText}
                                </AnimatedStatementText>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    alignment: PropTypes.string.isRequired,
    mainText: PropTypes.string.isRequired,
    subText: PropTypes.string,
    backgroundImage: PropTypes.object,
    backgroundImageMobile: PropTypes.object,
    theme: PropTypes.string
};

ModuleAnimatedStatementLarge.propTypes = propTypes;

export default ModuleAnimatedStatementLarge;
